















































































































































































import Vue from 'vue';
import { Component, Prop, Watch } from 'vue-property-decorator';
import { mapState, mapGetters } from 'vuex';
import { Route } from 'vue-router';
import { DailyChallenge, DcProblem, DcProblemLoggedOut } from 'frontend/types/daily-challenges';
import { apiResolve } from 'frontend/router';
import { postData } from 'frontend/utils/ajax';
import LoadingAnimation from 'frontend/components/LoadingAnimation.vue';
import DailyChallengeDisputes from 'frontend/views/dailyproblems/components/DailyChallengeDisputes.vue';
import DailyChallengeSolutions from 'frontend/views/dailyproblems/components/DailyChallengeSolutions.vue';
import { scrollToAnchorName } from 'frontend/utils/scroll';
import XPBadge from 'frontend/components/XPBadge.vue';

type SolutionDiscussionState = 'solutions' | 'disputes';

@Component({
  components: {
    DailyChallengeProblem,
    DailyChallengeDisputes,
    DailyChallengeSolutions,
    LoadingAnimation,
    XPBadge,
  },
  computed: {
    ...mapState('dailyChallenges', ['problem']),
    ...mapGetters('globals', ['isAuthenticated']),
  },
})
export default class DailyChallengeProblem extends Vue {
  @Prop(Object) private dailyChallenge!: DailyChallenge;
  @Prop(Boolean) private isLocked!: boolean;
  @Prop(Boolean) private isCompleted!: boolean;

  private isAuthenticated!: boolean;
  private problem!: DcProblem | DcProblemLoggedOut;
  private multipleAnswers: string[] = [];
  private singleAnswer: string | null = null;
  private submitting: boolean = false;
  private solutionDiscussionState: SolutionDiscussionState = 'solutions';
  private wasCompletedInThisSession: boolean = false;
  private xpValueBeforeAttempt?: number | null;

  private mounted() {
    this.xpValueBeforeAttempt = this.loggedInProblem.xp_value;
  }

  private get loggedInProblem(): DcProblem {
    return this.problem as DcProblem;
  }

  private get isXPDefined(): boolean {
    return this.loggedInProblem.xp_value !== null && this.loggedInProblem.xp_value !== undefined;
  }

  private get isEarnedXPDefined(): boolean {
    return this.loggedInProblem.earned_xp !== null && this.loggedInProblem.earned_xp !== undefined;
  }

  private get endstate(): { title: string; description?: string; xp?: number } {
    const leaguesEndstates = {
      correct: {
        title: 'Correct!',
        description: 'Come back tomorrow to earn XP on another daily challenge',
        xp: this.loggedInProblem.earned_xp || 0,
      },
      incorrect: {
        title: 'Incorrect',
        description: this.xpValueBeforeAttempt
          ? 'Try another problem to earn XP'
          : 'Come back tomorrow to earn XP on another daily challenge',
        xp: 0,
      },
      viewedExplanation: {
        title: 'You viewed the explanation',
        xp: 0,
      },
    };

    const noXPEndstates = {
      correct: {
        title: 'Correct!',
      },
      incorrect: {
        title: 'Incorrect',
      },
      viewedExplanation: {
        title: 'You viewed the explanation',
      },
    };

    const endstates = this.isEarnedXPDefined || this.isXPDefined ? leaguesEndstates : noXPEndstates;

    return this.loggedInProblem.answer_is_correct
      ? endstates.correct
      : this.loggedInProblem.tries_left === 0
      ? endstates.incorrect
      : endstates.viewedExplanation;
  }

  private async triggerLoggedOutForm(): Promise<void> {
    this.$store.dispatch('signupModal/signup');
  }

  private get apiUrl(): string {
    const apiParams = {
      name: 'api_dailyproblems_actions',
      params: {
        dcSlug: this.dailyChallenge.slug || String(this.dailyChallenge.id),
      },
    };
    return apiResolve(apiParams).href;
  }

  private get isMultiSelectProblem(): boolean {
    return this.problem.problem_type === 'multi_select';
  }

  private get hasMultipleChoices(): boolean {
    return ['multi_select', 'single_select'].includes(this.problem.problem_type);
  }

  private selectAnswer(rawAnswer: string, index: number = 0): void {
    if (this.isMultiSelectProblem) {
      const answer = String(index + 1);
      if (this.multipleAnswers.includes(answer)) {
        this.multipleAnswers = this.multipleAnswers.filter(a => a !== answer);
      } else {
        this.multipleAnswers.push(answer);
      }
    } else {
      this.singleAnswer = rawAnswer;
    }
  }

  private get userSelectedAnswers(): string[] {
    if (!this.isCompleted) {
      if (this.isMultiSelectProblem) {
        return this.multipleAnswers;
      }
      if (this.singleAnswer) {
        return [this.singleAnswer];
      }
      return [];
    }
    if (!this.isMultiSelectProblem) {
      if (this.loggedInProblem.answer_is_correct) {
        return [this.loggedInProblem.correct_answer];
      }
      return this.loggedInProblem.previous_guesses;
    }
    let multiAnswerString = '';
    if (this.loggedInProblem.answer_is_correct) {
      multiAnswerString = this.loggedInProblem.raw_correct_answer;
    } else if (this.loggedInProblem.previous_guesses.length) {
      multiAnswerString = this.loggedInProblem.previous_guesses[0];
    }
    return multiAnswerString.replace('__multi_select__', '').split(',');
  }

  private get selectedAnswer(): string | null {
    if (this.isMultiSelectProblem && this.multipleAnswers.length) {
      return `__multi_select__${this.multipleAnswers.join(',')}`;
    }
    if (this.singleAnswer) {
      return this.singleAnswer;
    }
    return null;
  }

  private async submitAnswer(): Promise<void> {
    if (!this.selectedAnswer) {
      return;
    }
    if (!this.isAuthenticated) {
      return this.triggerLoggedOutForm();
    }
    this.submitting = true;
    const data: any = {};
    data.intent = 'attempt';
    data.a_id = this.loggedInProblem.attempt_id;
    data.answer = this.selectedAnswer;
    const resp = await postData(this.apiUrl, data);
    if (resp.success) {
      this.$store.commit('dailyChallenges/setAttempt', resp.data);
    }
    this.submitting = false;
    await scrollToAnchorName('dp-user-solutions');
  }

  private async viewExplanation(): Promise<void> {
    if (this.loggedInProblem.solution_brief) {
      this.solutionDiscussionState = 'solutions';
      return;
    }
    this.submitting = true;
    const data: any = {};
    data.intent = 'solution_discussions';
    const resp = await postData(this.apiUrl, data);
    if (resp.success) {
      this.$store.commit('dailyChallenges/setAttempt', resp.data);
    }
    this.submitting = false;
    this.solutionDiscussionState = 'solutions';
    await scrollToAnchorName('dp-user-solutions');
  }

  private async viewReports(): Promise<void> {
    if (this.loggedInProblem.dispute_reports) {
      this.solutionDiscussionState = 'disputes';
      return;
    }
    this.submitting = true;
    const data: any = {};
    data.intent = 'dailyproblems_disputecomment';
    const resp = await postData(this.apiUrl, data);
    this.solutionDiscussionState = 'disputes';
    if (resp.success) {
      if (resp.data.dispute_reports) {
        this.$store.commit('dailyChallenges/setDcReports', resp.data.dispute_reports);
      }
    }
    this.submitting = false;
    await scrollToAnchorName('dp-dispute-reports');
  }

  private get correctMultipleAnswers(): string[] {
    if (this.isMultiSelectProblem) {
      return this.loggedInProblem.raw_correct_answer.replace('__multi_select__', '').split(',');
    }
    return [this.loggedInProblem.correct_answer];
  }

  private isCorrectMcqChoice(rawAnswer: string, index: number = 0): boolean {
    if (!this.isCompleted) {
      return false;
    }
    if (!this.isMultiSelectProblem) {
      return this.loggedInProblem.raw_correct_answer === rawAnswer;
    }
    return this.correctMultipleAnswers.includes(String(index + 1));
  }

  private isUserMcqChoice(rawAnswer: string, index: number = 0): boolean {
    const answer = this.isMultiSelectProblem ? String(index + 1) : rawAnswer;
    return this.userSelectedAnswers.includes(answer);
  }

  @Watch('$route', { immediate: true, deep: true })
  private async onDailyChallengeUpdate(newVal: Route) {
    this.solutionDiscussionState = 'solutions';
  }

  @Watch('isCompleted')
  private isCompletedChanged() {
    this.wasCompletedInThisSession = true;
  }
}
